/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.19.0
 */

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { icons } from '@assets/index';
import { Button } from '@components/common';
import { ButtonImageStyled } from '@components/common/Button/styled/ButtonStyled';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import Guard from '@modules/role/Guard';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { setCookie } from '@modules/storage/storageActions';
import { ECookieKey } from '@modules/storage/storageTypes';
import { getCookieByName } from '@modules/storage/storageUtils';
import { checkESTableList } from '@utils/checkESTable';

import { tableContext, tableListUrlSelector } from '../../selectors';
import { setTableSize } from '../../slice';
import { tableApi } from '../../store';

const TableDensity = () => {

    const [isDensity, setIsDensity] = useState<boolean>();

    const dispatch = useDispatch();

    const context = useSelector(tableContext);

    const listUrl = useSelector(tableListUrlSelector);

    const isDensityFromCookie: string = getCookieByName(ECookieKey.DENSITY_LIST_VIEW);
    const isDensityEnabled = useSelector(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.DensityListView));

    const translationCommon = useSelector(makeSelectTranslationCommon());
    const tTooltip = translationCommon.lists.densityListView.buttonToggle.tooltip;

    const onSetTableSize = (size: number) => {
        dispatch(setTableSize({ size }));
    };

    useEffect(() => {
        const checkDensity = isDensityEnabled && isDensityFromCookie === 'true';
        setIsDensity(checkDensity);
        onSetTableSize(checkDensity ? 32 : 24);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDensityEnabled, isDensityFromCookie]);

    const onClick = () => {
        if (!listUrl) {
            // eslint-disable-next-line no-throw-literal
            throw '[Table]: incorrect table configuration. Set meta and list url in initialize method';
        }

        const newDensity = !isDensity;

        dispatch(setCookie({
            key: ECookieKey.DENSITY_LIST_VIEW,
            value: newDensity,
            options: {
                'max-age': '7776000', // 3 months
            },
        }));

        setIsDensity(newDensity);

        const size = newDensity ? 32 : 24;
        onSetTableSize(newDensity ? 32 : 24);

        dispatch(
            tableApi.endpoints.getTableList.initiate({
                url: listUrl,
                from: 0,
                size,
                use_search_after: checkESTableList(listUrl),
                context,
            }, { forceRefetch: true }),
        );
    };

    return (
        <Guard accessKey={AccessComponentKeyEnum.DensityListView}>
            <Button
                className="density-button"
                dataName="refresh"
                height="32px"
                kind="circle"
                onClick={onClick}
            >
                <ArrowTooltip
                    dataName="tooltipDensity"
                    message={isDensity ? tTooltip.toDefault.text : tTooltip.toCompact.text}
                    placement="bottom-end"
                    title={isDensity ? tTooltip.toDefault.title : tTooltip.toCompact.title}
                >
                    <ButtonImageStyled
                        alt="density-icon"
                        src={isDensity ? icons.densityOff : icons.densityOn}
                    >
                    </ButtonImageStyled>
                </ArrowTooltip>
            </Button>
        </Guard>
    );
};

export default TableDensity;
